// ShareBite badge evaluation engine (Task 7.3).
//
// Pure, deterministic computation of the set of milestone badges a User holds.
// Badges are awarded automatically as Restaurants and NGOs reach donation and
// collection milestones. This module is the single source of truth for the
// milestone thresholds (BADGE_THRESHOLDS) and the evaluation rule; it is
// platform-independent and side-effect free. The caller (a Cloud Function run
// after a Donation reaches Completed) is responsible for persisting newly
// earned Achievements and emitting notifications.
//
// All metrics passed in MUST be derived from Completed donations/pickups only
// (Req 30.1). Non-Completed donations contribute nothing because they are
// excluded before metrics are computed; this engine never sees them.
//
// Design reference: design.md "Property 12: Badge awarding correctness and
// idempotence" and "AchievementService.evaluateBadges".
// Requirements:
//   30.1 - Metrics use Completed donations/pickups only; all others excluded.
//   30.2 - Restaurant Completed-donation count: 1 -> FirstDonation,
//          10 -> CommunityContributor.
//   30.3 - Restaurant total Completed meals: 100 -> FoodHero,
//          500 -> HungerFighter, 1000 -> ShareBiteChampion.
//   30.4 - NGO Completed-pickup count: 1 -> FirstCollection,
//          10 -> CommunityHelper.
//   30.5 - NGO total Completed meals received: 100 -> HungerReliefPartner,
//          500 -> ImpactMaker, 1000 -> ShareBiteChampion.
//   30.6 - The same badge is never awarded to a User more than once.
/**
 * The single source of truth for every milestone badge (Req 30.2 - 30.5).
 *
 * Order is canonical and stable; {@link evaluateBadges} returns badges in this
 * order for deterministic output. `ShareBiteChampion` is shared: both a
 * Restaurant (1,000 meals donated) and an NGO (1,000 meals received) earn it.
 */
export const BADGE_THRESHOLDS = [
    // Restaurant donation-count milestones (Req 30.2).
    { badgeId: "FirstDonation", roles: ["Restaurant"], metric: "count", threshold: 1 },
    { badgeId: "CommunityContributor", roles: ["Restaurant"], metric: "count", threshold: 10 },
    // Restaurant meals-donated milestones (Req 30.3).
    { badgeId: "FoodHero", roles: ["Restaurant"], metric: "meals", threshold: 100 },
    { badgeId: "HungerFighter", roles: ["Restaurant"], metric: "meals", threshold: 500 },
    // NGO pickup-count milestones (Req 30.4).
    { badgeId: "FirstCollection", roles: ["NGO"], metric: "count", threshold: 1 },
    { badgeId: "CommunityHelper", roles: ["NGO"], metric: "count", threshold: 10 },
    // NGO meals-received milestones (Req 30.5).
    { badgeId: "HungerReliefPartner", roles: ["NGO"], metric: "meals", threshold: 100 },
    { badgeId: "ImpactMaker", roles: ["NGO"], metric: "meals", threshold: 500 },
    // Shared 1,000-meal championship (Req 30.3 / Req 30.5).
    { badgeId: "ShareBiteChampion", roles: ["Restaurant", "NGO"], metric: "meals", threshold: 1000 },
];
/**
 * Return the exact set of badges whose milestone thresholds the given metrics
 * satisfy for the User's role, in canonical {@link BADGE_THRESHOLDS} order.
 *
 * This is the threshold-satisfaction core, independent of any prior state.
 */
function satisfiedBadges(metrics) {
    const earned = [];
    for (const t of BADGE_THRESHOLDS) {
        if (!t.roles.includes(metrics.role))
            continue;
        const value = t.metric === "count" ? metrics.completedCount : metrics.totalMeals;
        if (value >= t.threshold)
            earned.push(t.badgeId);
    }
    return earned;
}
/**
 * Evaluate the full set of badges a User holds after considering their
 * Completed-only metrics, returning a duplicate-free list in canonical order.
 *
 * The result is exactly the set of badges whose thresholds are satisfied by
 * `metrics`, unioned with any badges already held (`alreadyHeld`). Because
 * metrics never decrease over a User's lifetime, every already-held badge
 * remains satisfied, so the union equals the satisfied set; once earned, a
 * badge is never lost (Req 30.6).
 *
 * Idempotence: feeding the result back in as `alreadyHeld` with the same
 * metrics yields an identical set — no badge is added or duplicated. Likewise,
 * re-running with unchanged metrics awards nothing new (Req 30.6). Badges for
 * roles other than the User's own (e.g. NGO badges for a Restaurant) are never
 * awarded.
 *
 * This function is pure: it reads only its arguments and returns a fresh array
 * without mutating any input.
 *
 * @param metrics     Completed-only count and meals metrics plus the User role.
 * @param alreadyHeld Badges the User already holds (defaults to none).
 * @returns The complete, deduplicated set of held badges in canonical order.
 */
export function evaluateBadges(metrics, alreadyHeld = []) {
    const held = new Set(alreadyHeld);
    for (const badgeId of satisfiedBadges(metrics)) {
        held.add(badgeId);
    }
    // Emit in canonical BADGE_THRESHOLDS order for deterministic, dedup output.
    // ShareBiteChampion appears once in the table, so a single pass dedupes it.
    const order = [];
    const seen = new Set();
    for (const t of BADGE_THRESHOLDS) {
        if (held.has(t.badgeId) && !seen.has(t.badgeId)) {
            seen.add(t.badgeId);
            order.push(t.badgeId);
        }
    }
    return order;
}
//# sourceMappingURL=badges.js.map